import React from 'react';
import { 
  Users, 
  Settings, 
  Database, 
  FileText, 
  BarChart2, 
  MessageSquare, 
  BookOpen, 
  Layers, 
  Shield,
  GraduationCap,
  Table,
  Terminal,
  RefreshCw,
  Bot
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';

export type AdminSection = 
  | 'users' 
  | 'add-user'
  | 'app-rollout'
  | 'metrics'
  | 'categories'
  | 'seeds'
  | 'plms'
  | 'pmt2'
  | 'sops'
  | 'sop-summary'
  | 'lessons'
  | 'glossary'
  | 'feedback'
  | 'feedback-notif'
  | 'firestore'
  | 'firestore'
  | 'cache'
  | 'sophie';

interface AdminSidebarProps {
  activeSection: AdminSection;
  onSelectSection: (section: AdminSection) => void;
  className?: string;
}

interface SidebarItem {
  id: AdminSection;
  label: string;
  icon: React.ElementType;
}

interface SidebarGroup {
  title: string;
  items: SidebarItem[];
}

const sidebarGroups: SidebarGroup[] = [
  {
    title: 'User Management',
    items: [
      { id: 'users', label: 'Authorized Emails', icon: Users },
      { id: 'add-user', label: 'Add New User', icon: Shield },
      { id: 'app-rollout', label: 'App Rollout', icon: Layers },
    ]
  },
  {
    title: 'Content & Knowledge',
    items: [
      { id: 'categories', label: 'Categories', icon: Layers },
      { id: 'seeds', label: 'Idea Seeds', icon: Database },
      { id: 'sops', label: 'SOP Management', icon: FileText },
      { id: 'sop-summary', label: 'SOP Summaries', icon: BookOpen },
      { id: 'glossary', label: 'Glossary', icon: BookOpen },
      { id: 'lessons', label: 'AI DevQuest', icon: GraduationCap },
      { id: 'sophie', label: 'Sophie AI', icon: Bot },
    ]
  },
  {
    title: 'System & Analytics',
    items: [
      { id: 'metrics', label: 'Usage Metrics', icon: BarChart2 },
      { id: 'feedback', label: 'Feedback', icon: MessageSquare },
      { id: 'feedback-notif', label: 'Feedback Recipients', icon: Users },
      { id: 'plms', label: 'PLMS Workbook', icon: Table },
      { id: 'pmt2', label: 'PMT2 Config', icon: Settings },
    ]
  },
  {
    title: 'Advanced',
    items: [
      { id: 'firestore', label: 'Firestore Debug', icon: Terminal },
      { id: 'cache', label: 'Cache Backfill', icon: RefreshCw },
    ]
  }
];

export const AdminSidebar: React.FC<AdminSidebarProps> = ({ 
  activeSection, 
  onSelectSection,
  className 
}) => {
  return (
    <div className={cn("pb-12 w-64 border-r bg-card h-screen sticky top-0", className)}>
      <div className="space-y-4 py-4">
        <div className="px-3 py-2">
          <h2 className="mb-2 px-4 text-lg font-semibold tracking-tight">
            Admin Panel
          </h2>
          <ScrollArea className="h-[calc(100vh-100px)] px-1">
            <div className="space-y-1">
              {sidebarGroups.map((group, i) => (
                <div key={i} className="mb-6">
                  <h3 className="mb-2 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {group.title}
                  </h3>
                  <div className="space-y-1">
                    {group.items.map((item) => (
                      <Button
                        key={item.id}
                        variant={activeSection === item.id ? "secondary" : "ghost"}
                        className={cn(
                          "w-full justify-start",
                          activeSection === item.id && "bg-secondary/50 font-medium"
                        )}
                        onClick={() => onSelectSection(item.id)}
                      >
                        <item.icon className="mr-2 h-4 w-4" />
                        {item.label}
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
};
